import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid } from '@material-ui/core';
import moment from 'moment';
import { globalGetService } from '../../../../globalServices';
import { PageLoader } from '../../../shared_elements';
import { checkApiStatus } from '../../../utils_v2';
import ApplicationsActivityCard from '../components/ApplicationsActivityCard';
import RecentReleasesCard from '../components/RecentReleasesCard';
import ReleaseTimeline from '../components/ReleaseTimeline';
import UpcomingFeaturesCard from '../components/UpcomingFeaturesCard';
import WhatsNewCard from '../components/WhatsNewCard';
import WhatsNewToolbar from '../components/WhatsNewToolbar';
import { applicationColors } from '../config/whatsNewConfig';
import { getReleaseAccent, getReleaseArtwork, getReleaseEnhancement, getReleaseStatus, searchableText } from '../utils/releaseUtils';
import '../styles/whatsNew.scss';

const getApplicationValue = (item) => item?.application?.value
    ?? item?.application?.id
    ?? item?.application_id;

const lmApplicationId = 33;
const contractApplicationId = 3;
const lmLabel = 'Lease Management';

const getInitialApplication = () => new URLSearchParams(window.location.search).get('application') || 'all';

const normalizeApplication = (item) => {
    if (Number(getApplicationValue(item)) !== contractApplicationId) return item;
    return {
        ...item,
        application_id: lmApplicationId,
        application: {
            ...item.application,
            value: lmApplicationId,
            label: lmLabel
        }
    };
};

const WhatsNew = () => {
    const [whatsNewData, setWhatsNewData] = useState({ data: [], latest_release_date: null, coming_up_next: [] });
    const [isLoading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedApplication, setSelectedApplication] = useState(getInitialApplication);

    useEffect(() => {
        getFeatureList();
    }, []);

    const getFeatureList = () => {
        setLoading(true);
        globalGetService('/api/features/', { application: 1 })
            .then(response => {
                setLoading(false);
                if (checkApiStatus(response)) {
                    const latestReleaseDate = response?.data?.latest_release_date;
                    setWhatsNewData({
                        ...response.data,
                        data: Array.isArray(response?.data?.data) ? response.data.data.map(normalizeApplication) : [],
                        latest_release_date: latestReleaseDate ? {
                            ...latestReleaseDate,
                            label: latestReleaseDate.label === 'Contracts' ? lmLabel : latestReleaseDate.label
                        } : null,
                        coming_up_next: Array.isArray(response?.data?.coming_up_next) ? response.data.coming_up_next : []
                    });
                }
            });
    };

    const sortedUpdates = useMemo(() => [...whatsNewData.data].sort((first, second) => (
        moment(second?.release_date).valueOf() - moment(first?.release_date).valueOf()
    )), [whatsNewData.data]);

    const applicationFilters = useMemo(() => {
        const applications = sortedUpdates.reduce((result, item) => {
            const applicationValue = getApplicationValue(item);
            const applicationLabel = item?.application?.label;
            if (applicationValue == null || !applicationLabel) return result;
            const value = String(applicationValue);
            if (!result.some(application => application.value === value)) {
                result.push({ value, label: applicationLabel });
            }
            return result;
        }, []);

        return [{ value: 'all', label: 'All' }, ...applications];
    }, [sortedUpdates]);

    const activeApplication = applicationFilters.find(filter => filter.value === selectedApplication);
    const normalizedSearch = searchValue.trim().toLowerCase();
    const filteredUpdates = useMemo(() => sortedUpdates.filter(item => {
        const text = searchableText(item);
        return text.includes(normalizedSearch)
            && (selectedApplication === 'all' || String(getApplicationValue(item)) === selectedApplication);
    }), [normalizedSearch, selectedApplication, sortedUpdates]);

    const getApplicationCount = useCallback(filter => filter.value === 'all'
        ? sortedUpdates.length
        : sortedUpdates.filter(item => String(getApplicationValue(item)) === filter.value).length, [sortedUpdates]);

    const applicationActivity = useMemo(() => {
        const today = moment().endOf('day');
        const start = moment().subtract(90, 'days').startOf('day');
        const recentUpdates = sortedUpdates.filter(item => moment(item?.release_date).isBetween(start, today, undefined, '[]'));
        return applicationFilters.slice(1).map((application, index) => ({
            name: application.label,
            color: applicationColors[index % applicationColors.length],
            count: recentUpdates.filter(item => String(getApplicationValue(item)) === application.value).length
        }));
    }, [applicationFilters, sortedUpdates]);

    const latestUpdate = sortedUpdates[0];
    const latestArtwork = whatsNewData.latest_release_date ? getReleaseArtwork(whatsNewData.latest_release_date) : null;
    const selectedApplicationArtwork = selectedApplication !== 'all' && activeApplication
        ? getReleaseArtwork(sortedUpdates.find(item => String(getApplicationValue(item)) === selectedApplication))
        : null;
    const scrollToReleases = () => document.getElementById('release-highlights')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const handleSearchChange = (value) => {
        setSearchValue(value);
        if (!value.trim()) setSelectedApplication('all');
    };

    return (
        <Box className="whats-new">
            <WhatsNewCard
                latestArtwork={latestArtwork}
                latestUpdate={whatsNewData.latest_release_date}
                sortedUpdatesCount={sortedUpdates.length}
                activeApplicationsCount={applicationFilters.length - 1}
                onExploreClick={scrollToReleases}
            />
            <Box className="whats-new-content">
                <WhatsNewToolbar
                    searchValue={searchValue}
                    onSearchChange={handleSearchChange}
                    applicationFilters={applicationFilters}
                    selectedApplication={selectedApplication}
                    onSelectApplication={setSelectedApplication}
                    getApplicationCount={getApplicationCount}
                />
                <Box className="whats-new-body">
                    <Grid container spacing={3} alignItems="flex-start">
                        <Grid item xs={12} lg={8}>
                            <ReleaseTimeline
                                filteredUpdates={filteredUpdates}
                                sortedUpdatesLength={sortedUpdates.length}
                                latestUpdate={latestUpdate}
                                isLoading={isLoading}
                                getReleaseAccent={getReleaseAccent}
                                selectedApplication={selectedApplication !== 'all' ? activeApplication : null}
                                selectedApplicationArtwork={selectedApplicationArtwork}
                                getReleaseArtwork={getReleaseArtwork}
                                getReleaseStatus={getReleaseStatus}
                                getReleaseEnhancement={getReleaseEnhancement}
                            />
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <Box className="whats-new-sidebar">
                                <UpcomingFeaturesCard features={whatsNewData.coming_up_next} />
                                <RecentReleasesCard updates={sortedUpdates} />
                                <ApplicationsActivityCard applications={applicationActivity} />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            {isLoading ? <PageLoader /> : null}
        </Box>
    );
};

export default WhatsNew;
